import { useEffect, useState } from 'react';
import Link from 'next/link';
import useWindowSize from '@hooks/useWindowSize';
import { toPlayerByIdPath } from '@lib/page-utils/player-profile/slugs';

const MOBILE_BREAKPOINT = 980;
const MOBILE_NAME_LIMIT = 10;

type NameProps = {
  item: {
    displayName?: string;
    team?: { shortName?: string };
    /**
     * Cross-season Opta id. Unlike the row's `id`, which is re-minted every
     * season, this is what the profile route resolves, so it is the only
     * usable link target. Absent on team rows, which have no profile.
     */
    optaUuid?: string;
  };
};

const PlayerName = ({ item }: NameProps) => {
  const { width } = useWindowSize();
  const [name, setName] = useState('');
  const itemName = item.displayName ?? '';

  useEffect(() => {
    if (itemName && width <= MOBILE_BREAKPOINT) {
      setName(
        `${itemName.slice(0, MOBILE_NAME_LIMIT)}${
          itemName.length > MOBILE_NAME_LIMIT ? '...' : ''
        }`
      );
    } else {
      setName(itemName);
    }
  }, [width, itemName]);

  const teamShort = item.team?.shortName ?? '';

  const label = (
    <>
      <span className="text-xs font-semibold tracking-tight">{name}</span>
      {teamShort && (
        <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-neutral-400">
          {teamShort}
        </span>
      )}
    </>
  );

  if (!item.optaUuid) {
    return (
      <span className="inline-flex flex-col leading-tight text-neutral-800">
        {label}
      </span>
    );
  }

  // A real anchor rather than a click handler, so middle-click, open-in-new-tab
  // and keyboard activation all behave as the row looks like they should.
  //
  // The by-id resolver rather than the legacy /player-profile?id= URL: that
  // URL is a next.config.js redirect, which only a full page load follows. An
  // in-app click fetches the page's data JSON instead, the redirect answers
  // with a bare 308 the router cannot follow, and the profile mounts with no
  // props (FFH-2848). The resolver redirects from getServerSideProps, which
  // the router does follow.
  return (
    <Link
      href={toPlayerByIdPath(item.optaUuid)}
      className="inline-flex flex-col leading-tight text-neutral-800 hover:text-primary-700 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
    >
      {label}
    </Link>
  );
};

export default PlayerName;
